import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, MapPin } from 'lucide-react';

export const Footer = () => {
  const { t, isArabic } = useLanguage();
  const navigate = useNavigate();
  const [clickCount, setClickCount] = useState(0);
  const clickTimeoutRef = useRef(null);

  const handleLogoClick = () => {
    // Clear previous timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    // If 6 clicks, navigate to admin
    if (newCount >= 6) {
      setClickCount(0);
      navigate('/admin');
      return;
    }
    
    // Reset count after 2 seconds of no clicks
    clickTimeoutRef.current = setTimeout(() => {
      setClickCount(0);
    }, 2000);
  };

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('about') },
    { path: '/products', label: t('products') },
    { path: '/contracts', label: t('contracts') },
    { path: '/contact', label: t('contact') },
  ];

  return (
    <footer className="bg-black text-white" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_4c33b93d-22ff-46d7-a5ee-41ef8de79233/artifacts/rnh8b6ue_%D8%A7%D9%84%D8%B7%D8%A8%D9%8A%D8%A9321.jpg" 
                alt="البصمة الحديثة لاستيراد المعدات الطبية - ALBASMA ALHADITHA"
                className="h-14 w-auto cursor-pointer select-none"
                onClick={handleLogoClick}
                data-testid="footer-logo"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              {isArabic 
                ? 'شريكك الموثوق في استيراد وتوزيع المعدات الطبية عالية الجودة في ليبيا'
                : 'Your trusted partner for importing and distributing high-quality medical equipment in Libya'
              }
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-semibold mb-6 text-white">{t('quickLinks')}</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white text-sm transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2">
            <h3 className="text-base font-semibold mb-6 text-white">{t('contactInfo')}</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-1">{t('phone')}</span>
                  <a href="tel:0943999339" className="text-white hover:text-slate-300 transition-colors text-sm font-medium" dir="ltr">
                    0943 999 339
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-1">{t('email')}</span>
                  <a href="mailto:albasma.alhadithaly@gmail.com" className="text-white hover:text-slate-300 transition-colors text-sm font-medium">
                    albasma.alhadithaly@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-1">{t('address')}</span>
                  <span className="text-white text-sm font-medium">{t('addressValue')}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">{t('copyright')}</p>
            <p className="text-slate-500 text-xs">{t('freeDelivery')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
